import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faClockRotateLeft,
  faXmark,
  faShieldHalved,
  faTriangleExclamation,
  faRotate,
  faSpinner,
  faFileCsv,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons"

import { Modal, Tooltip } from "antd"

import { useDraggablePanel } from "hooks/core/useDraggablePanel"
import { exportToCsv } from "utils/exportToCsv"
import { notify } from "shared/ui/Notifications/notify"
import { AuditRecord } from "types/questionnaire.types"
import { HandledApiError } from "api/queryClient"
import ConfirmationModal from "shared/ui/ConfimationModal/ConfimationModal.component"
import { useQuestionnaireAudit } from "../api/useQuestionnaireAudit"
import {
  useProjectAuditQuery,
  useProjectAuditVerifyQuery,
} from "../api/useProvenanceQueries"
import { useDeliverableLineageQuery } from "../api/useLineage"
import { LazyLineageView } from "./LazyLineageView.component"
import { HistoryRecordItem } from "./HistoryRecordItem.component"
import {
  entryCountLabel,
  isVersionRecord,
  seqLabel,
  verifyStateOf,
  versionNumbers,
  withoutCodes,
} from "../utils/historyLabels"
import { isRefusedRequest } from "../utils/refusedRequest"

interface VersionHistoryPanelProps {
  isVisible: boolean
  /** The open project (its owner comes from the project store). */
  projectId: string
  onClose?: () => void
  /** Restore a version's content into the editor (creates a new version on save). */
  onRestore?: (content: string, seq: number) => void | Promise<void>
  /** Shown where the restore button would be when restore isn't offered here (e.g. on a variant). */
  restoreHint?: string
  /** Jump the editor to a question by label (best-effort; only the open project). */
  onJumpToQuestion?: (label: string) => void
  /** Render embedded (e.g. in the Provenance tab) instead of as a floating, draggable panel. */
  inline?: boolean
  /**
   * Open this record in the questionnaire editor (inline timeline only: restoring changes the
   * questionnaire, so it is done in the editor).
   */
  onOpenInEditor?: (seq: number) => void
  /** Switch to the ancestry view (Provenance tab only, omitted elsewhere). */
  onViewAncestry?: () => void
  /** Scroll to + expand this record when the chain loads (editor deep-link target). */
  focusSeq?: number | null
}

// The rawHash (SHA-256) of an empty questionnaire: only a version recorded empty is
// restored from an empty copy
const EMPTY_TEXT_HASH = "e3b0c44298fc1c149afbf4c8996fb92427ae41e4649b934ca495991b7852b855"

// Timeline event-kind filter + windowed rendering.
const PAGE_SIZE = 50
type TimelineFilter = "all" | "versions" | "imports" | "exports" | "waves" | "merges"
const TIMELINE_FILTERS: { value: TimelineFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "versions", label: "Versions" },
  { value: "imports", label: "Imports" },
  { value: "exports", label: "Exports" },
  { value: "waves", label: "Waves" },
  { value: "merges", label: "Merges" },
]
const matchesTimelineFilter = (action: string, filter: TimelineFilter): boolean => {
  switch (filter) {
    case "versions":
      return isVersionRecord(action)
    case "imports":
      return action === "data.imported"
    case "exports":
      return action === "deliverable.exported"
    case "waves":
      return action.startsWith("wave.")
    case "merges":
      return action === "data.merged"
    default:
      return true
  }
}

// ---- panel ----------------------------------------------------------------

export const VersionHistoryPanel: React.FC<VersionHistoryPanelProps> = ({
  isVisible,
  projectId,
  onClose,
  onRestore,
  restoreHint,
  onJumpToQuestion,
  inline = false,
  onOpenInEditor,
  onViewAncestry,
  focusSeq = null,
}) => {
  const panelRef = useRef<HTMLDivElement>(null)
  const { position, isDragging, handleHeaderMouseDown } = useDraggablePanel()
  const { getSnapshotById } = useQuestionnaireAudit()

  const [expandedSeq, setExpandedSeq] = useState<number | null>(null)
  const [restoringSeq, setRestoringSeq] = useState<number | null>(null)
  // A restore runs at a time: the ref also stops a second click before the buttons re-render
  const restoringRef = useRef(false)
  // The version waiting for the user to confirm its restore
  const [pendingRestore, setPendingRestore] = useState<AuditRecord | null>(null)
  // Captured-lineage viewer (deliverable.exported records with a lineage snapshot): the hash of
  // the graph shown, null while the viewer is closed.
  const [lineageHash, setLineageHash] = useState<string | null>(null)
  const lineageQuery = useDeliverableLineageQuery(lineageHash)

  // Cached reads: a checkpoint save, data import or recorded export invalidates them, so the
  // timeline refreshes itself; the Refresh button just refetches.
  const auditQuery = useProjectAuditQuery(projectId, { enabled: isVisible })
  const verifyQuery = useProjectAuditVerifyQuery(projectId, { enabled: isVisible })
  const records = useMemo(
    () => auditQuery.data?.records ?? [],
    [auditQuery.data]
  )
  const verify = verifyQuery.data ?? null
  // A verify that couldn't run (or failed outright) is "unavailable", never tampering; one the
  // server refused is not asked again.
  const verifyState = verifyStateOf(
    verify,
    verifyQuery.isError,
    isRefusedRequest(verifyQuery.error)
  )
  const noVerdict = verifyState === "unavailable" || verifyState === "refused"
  const loading = auditQuery.isLoading

  // Expand the newest record whenever the chain content changes.
  useEffect(() => {
    if (records.length) setExpandedSeq(records[records.length - 1].seq)
  }, [records])

  // Editor deep-link target ("Open in editor" from the Provenance timeline): expand + scroll to
  // the requested record. Runs after the expand-newest effect, so the focus wins.
  useEffect(() => {
    if (focusSeq == null || !records.some((r) => r.seq === focusSeq)) return
    setExpandedSeq(focusSeq)
    // Next frame, so the expanded row exists before we scroll.
    requestAnimationFrame(() => {
      document
        .getElementById(`vh-rec-${focusSeq}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" })
    })
  }, [focusSeq, records])

  const load = useCallback(() => {
    auditQuery.refetch()
    verifyQuery.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auditQuery.refetch, verifyQuery.refetch])

  // Escape to close (floating panel only — inline has no close).
  useEffect(() => {
    if (!isVisible || inline) return
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose?.()
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isVisible, inline, onClose])

  // newest first for display
  const ordered = useMemo(() => [...records].sort((a, b) => b.seq - a.seq), [records])
  const latestSeq = ordered.length ? ordered[0].seq : null

  // Event-kind filter + windowed rendering (chains grow one record per save/import/export).
  const [filter, setFilter] = useState<TimelineFilter>("all")
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)
  useEffect(() => setVisibleCount(PAGE_SIZE), [filter, projectId])
  const filtered = useMemo(
    () => ordered.filter((r) => matchesTimelineFilter(r.action, filter)),
    [ordered, filter]
  )
  const visibleRecords = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  )

  // Number only real questionnaire versions (ascending) and mark the current one, so events
  // (data imports, tracker links, deliverable exports) aren't labelled "Version N" / "current".
  const numBySeq = useMemo(() => versionNumbers(records), [records])
  // The map is filled in seq order, so its last key is the newest version.
  const lastVersionSeq = useMemo(() => Array.from(numBySeq.keys()).pop() ?? null, [numBySeq])

  // Restores a version from its saved copy. A failed save has already been reported (it
  // rejects with a HandledApiError); anything else is reported here.
  const handleRestore = useCallback(
    async (rec: AuditRecord) => {
      if (!onRestore || restoringRef.current) return
      restoringRef.current = true
      setRestoringSeq(rec.seq)
      try {
        let content: unknown
        try {
          content = (await getSnapshotById(projectId, rec.rawHash)).data
        } catch {
          notify.error("Couldn't restore this version", "Its saved copy couldn't be read.")
          return
        }
        // A missing copy comes back empty (or not as text). Restoring that would empty the
        // questionnaire and drop every variant.
        if (
          typeof content !== "string" ||
          (content === "" && rec.rawHash.toLowerCase() !== EMPTY_TEXT_HASH)
        ) {
          notify.error("Couldn't restore this version", "Its saved copy wasn't found.")
          return
        }
        await onRestore(content, rec.seq)
      } catch (e) {
        if (!(e instanceof HandledApiError)) {
          console.error("Restoring a version failed:", e)
          notify.error("Couldn't restore this version", "Please try again.")
        }
      } finally {
        restoringRef.current = false
        setRestoringSeq(null)
      }
    },
    [onRestore, getSnapshotById, projectId]
  )

  const confirmRestore = useCallback(() => {
    const rec = pendingRestore
    setPendingRestore(null)
    if (rec) void handleRestore(rec)
  }, [pendingRestore, handleRestore])

  // Export the whole audit log as a flat CSV: one row per metadata change, carrying its
  // version context. Versions with no field-level changes still emit one summary row so the
  // log is complete. Reuses the shared RFC-4180 CSV writer (no hand-rolled escaping).
  const handleExportCsv = useCallback(() => {
    if (!records.length) return
    type Row = {
      version: number
      timestamp: string
      performedBy: string
      action: string
      parseStatus: string
      changeKind: string
      field: string
      path: string
      oldValue: string
      newValue: string
      recordHash: string
      metadataHash: string
    }
    const rows: Row[] = []
    for (const rec of ordered) {
      const base = {
        version: rec.seq,
        timestamp: rec.performedAt,
        performedBy: rec.performedByName || rec.performedBy,
        action: rec.action,
        parseStatus: rec.parseStatus,
        recordHash: rec.recordHash,
        metadataHash: rec.metadataHash,
      }
      if (rec.changes && rec.changes.length > 0) {
        // Every change, but never an answer code
        for (const c of rec.changes.map(withoutCodes)) {
          rows.push({
            ...base,
            changeKind: c.kind,
            field: c.label || c.path,
            path: c.path,
            oldValue: c.oldValue ?? "",
            newValue: c.newValue ?? "",
          })
        }
      } else {
        rows.push({
          ...base,
          changeKind: rec.parseStatus === "parse_failed" ? "parse_failed" : "(no metadata changes)",
          field: "",
          path: "",
          oldValue: "",
          newValue: "",
        })
      }
    }
    const date = new Date().toISOString().split("T")[0]
    exportToCsv(rows, `version-history-${projectId}-${date}`, {
      columns: [
        "version", "timestamp", "performedBy", "action", "parseStatus",
        "changeKind", "field", "path", "oldValue", "newValue", "recordHash", "metadataHash",
      ],
      headers: [
        "Version", "Timestamp (UTC)", "Performed By", "Action", "Parse Status",
        "Change Kind", "Field", "Path", "Old Value", "New Value", "Record Hash", "Metadata Hash",
      ],
    })
  }, [records, ordered, projectId])

  // Header icon-buttons: light (slate) when embedded in a tab, white-on-gradient when floating.
  // Inline (the Provenance tab) the header buttons are the shared quiet icon buttons; on the
  // floating panel's brand header they stay white, at the same 28px square.
  const hdrBtn = inline
    ? "dq-btn dq-btn--icon dq-btn--quiet"
    : "inline-flex h-7 w-7 items-center justify-center rounded-md text-white/90 transition-colors hover:bg-white/15"

  if (!isVisible) return null

  return (
    <>
    <div
      ref={panelRef}
      className={
        inline
          ? "flex h-full w-full flex-col overflow-hidden bg-white"
          : "z-[1000] flex flex-col overflow-hidden rounded-xl bg-white shadow-2xl"
      }
      style={
        inline
          ? {}
          : {
              position: "fixed",
              left: position.x ? `${position.x}px` : undefined,
              top: position.y ? `${position.y}px` : undefined,
              right: position.x ? undefined : "24px",
              bottom: position.x ? undefined : "24px",
              width: "420px",
              maxHeight: "calc(100vh - 120px)",
              border: "1px solid #D1D5DB",
              cursor: isDragging ? "grabbing" : undefined,
            }
      }
    >
      {/* header */}
      <div
        className={
          inline
            ? "flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3"
            : "flex cursor-grab items-center justify-between px-4 py-3 text-white"
        }
        style={inline ? undefined : { background: "linear-gradient(135deg, var(--dq-brand), var(--dq-brand-hover))" }}
        onMouseDown={inline ? undefined : handleHeaderMouseDown}
      >
        <div className={`flex items-center gap-2 ${inline ? "text-slate-700" : ""}`}>
          <FontAwesomeIcon icon={faClockRotateLeft} className={inline ? "text-slate-400" : ""} />
          <span className="font-semibold">Project history</span>
          {records.length > 0 && (
            <span
              className={
                inline
                  ? "rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-500"
                  : "rounded-full bg-white/20 px-2 py-0.5 text-xs"
              }
            >
              {entryCountLabel(records.length)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={records.length === 0}
            title="Export the history as CSV"
            className={`${hdrBtn} disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <FontAwesomeIcon icon={faFileCsv} />
          </button>
          <button type="button" onClick={load} title="Refresh" className={hdrBtn}>
            <FontAwesomeIcon icon={faRotate} className={loading ? "animate-spin" : ""} />
          </button>
          {!inline && (
            <button type="button" onClick={onClose} title="Close" className={hdrBtn}>
              <FontAwesomeIcon icon={faXmark} />
            </button>
          )}
        </div>
      </div>

      {/* The history's check. When no verdict came back (Repos unreachable, request failed or
          refused) it says so, in amber, not red. */}
      {verifyState && (
        <div
          role="status"
          className={`py-2 text-[12px] font-medium ${
            verifyState === "ok"
              ? "bg-emerald-50 text-emerald-700"
              : noVerdict
                ? "bg-amber-50 text-amber-800"
                : "bg-rose-50 text-rose-700"
          }`}
        >
          <div className={`flex items-center gap-2 px-4 ${inline ? "mx-auto max-w-3xl" : ""}`}>
            <FontAwesomeIcon
              icon={
                verifyState === "ok"
                  ? faShieldHalved
                  : noVerdict
                    ? faCircleInfo
                    : faTriangleExclamation
              }
            />
            {verifyState === "unavailable" ? (
              <span>Couldn&apos;t check the history right now. It will be checked again shortly.</span>
            ) : verifyState === "refused" ? (
              <span>Couldn&apos;t check the history.</span>
            ) : verifyState === "unreadable" ? (
              <span>The history file can&apos;t be read, so it can&apos;t be checked.</span>
            ) : verifyState === "broken" ? (
              <span>History changed outside DATA(Q) at {seqLabel(verify?.firstBadSeq, numBySeq, records)}</span>
            ) : (
              <span>
                <Tooltip title="Each entry is linked to the one before it, so changing any past entry breaks the links after it. Verified: no entry has been changed.">
                  <span className="cursor-help underline decoration-dotted underline-offset-2">
                    History verified
                  </span>
                </Tooltip>{" "}
                · {entryCountLabel(verify?.recordsChecked ?? 0)} intact
              </span>
            )}
          </div>
        </div>
      )}

      {/* body */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className={inline ? "mx-auto max-w-3xl" : ""}>
        {ordered.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter timeline events">
            {TIMELINE_FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                onClick={() => setFilter(f.value)}
                aria-pressed={filter === f.value}
                className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  filter === f.value
                    ? "border-[var(--dq-brand)] bg-[var(--dq-brand-soft)] text-slate-800"
                    : "border-slate-200 text-slate-500 hover:bg-slate-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
        {loading && records.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
            <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
            <span className="text-[13px]">Loading versions…</span>
          </div>
        ) : auditQuery.isError ? (
          <div className="py-10 text-center text-[13px] text-rose-500">
            Couldn&apos;t load version history.
          </div>
        ) : ordered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-slate-400">
            <FontAwesomeIcon icon={faClockRotateLeft} className="h-6 w-6" />
            <span className="text-[13px]">No versions yet.</span>
            <span className="max-w-[260px] text-[12px]">Saved versions of the questionnaire are listed here. Save (Ctrl+S) to start the history.</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-[13px] text-slate-400">
            No {filter} events in this history.
          </div>
        ) : (
          <>
            <ul>
              {visibleRecords.map((rec) => (
                <HistoryRecordItem
                  key={rec.recordHash}
                  rec={rec}
                  isLatest={rec.seq === latestSeq}
                  isCurrentVersion={rec.seq === lastVersionSeq}
                  versionNumber={numBySeq.get(rec.seq)}
                  isBroken={verifyState === "broken" && verify?.firstBadSeq === rec.seq}
                  expanded={expandedSeq === rec.seq}
                  onToggle={() => setExpandedSeq((s) => (s === rec.seq ? null : rec.seq))}
                  onRestore={onRestore ? () => setPendingRestore(rec) : undefined}
                  restoreHint={restoreHint}
                  restoring={restoringSeq === rec.seq}
                  restoreDisabled={restoringSeq !== null}
                  onJumpToQuestion={onJumpToQuestion}
                  onViewLineage={setLineageHash}
                  onViewAncestry={onViewAncestry}
                  onOpenInEditor={onOpenInEditor ? () => onOpenInEditor(rec.seq) : undefined}
                />
              ))}
            </ul>
            {filtered.length > visibleCount && (
              <div className="pt-2 text-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                  className="dq-btn"
                >
                  Show {Math.min(PAGE_SIZE, filtered.length - visibleCount)} more (
                  {filtered.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </div>

      <div className="border-t border-slate-100 px-4 py-2 text-center text-[11px] text-slate-400">
        Tamper-evident project history
        {!inline && (
          <>
            {" "}· press <kbd className="rounded bg-slate-100 px-1">Esc</kbd> to close
          </>
        )}
      </div>
    </div>

    {/* Captured-lineage viewer: the derivation DAG snapshotted when the deliverable shipped */}
    <Modal
      title="Captured lineage, as exported"
      open={lineageHash !== null}
      onCancel={() => setLineageHash(null)}
      footer={null}
      width="88vw"
      styles={{ body: { height: "72vh", padding: 0 } }}
      destroyOnClose
    >
      {lineageQuery.data ? (
        <LazyLineageView dag={lineageQuery.data} />
      ) : lineageQuery.isError ? (
        <div className="flex h-full items-center justify-center text-[13px] text-slate-500">
          Couldn&apos;t load the captured lineage.
        </div>
      ) : (
        <div className="flex h-full items-center justify-center text-slate-400">
          <FontAwesomeIcon icon={faSpinner} className="h-5 w-5 animate-spin" />
        </div>
      )}
    </Modal>

    <ConfirmationModal
      isVisible={pendingRestore !== null}
      title="Restore this version?"
      message="Your current text is kept as a version first."
      confirmButtonText="Restore"
      confirmButtonType="primary"
      cancel={() => setPendingRestore(null)}
      confirm={confirmRestore}
    />
    </>
  )
}

export default VersionHistoryPanel
